import type { FormEvent, ReactElement } from 'react';
import { useEffect, useState } from 'react';
import type { AdminSpecialization } from '@cookspace-shared-contracts/core';

import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Switch } from '@/components/Switch';

const SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

interface SpecializationFormState {
    name: string;
    slug: string;
    sortOrder: string;
    isActive: boolean;
    canPublishRecipes: boolean | null;
}

interface SpecializationFormErrors {
    name?: string;
    slug?: string;
}

export interface SpecializationFormResult {
    name: string;
    slug: string;
    sortOrder: number;
    isActive: boolean;
    canPublishRecipes: boolean | null;
}

interface SpecializationEditModalProps {
    isOpen: boolean;
    editing: AdminSpecialization | null;
    /** Узел-родитель, под которым создаётся новый. null — корневой уровень. */
    parent: AdminSpecialization | null;
    isSaving?: boolean;
    submitError?: string | null;
    onClose: () => void;
    onSubmit: (values: SpecializationFormResult) => void;
}

const EMPTY_FORM: SpecializationFormState = {
    name: '',
    slug: '',
    sortOrder: '0',
    isActive: true,
    canPublishRecipes: null
};

export function SpecializationEditModal({
    isOpen,
    editing,
    parent,
    isSaving = false,
    submitError = null,
    onClose,
    onSubmit
}: SpecializationEditModalProps): ReactElement {
    const [form, setForm] = useState<SpecializationFormState>(EMPTY_FORM);
    const [errors, setErrors] = useState<SpecializationFormErrors>({});

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setErrors({});
        if (editing !== null) {
            setForm({
                name: editing.name,
                slug: editing.slug,
                sortOrder: String(editing.sortOrder),
                isActive: editing.isActive,
                canPublishRecipes: editing.canPublishRecipes
            });
        } else {
            setForm({ ...EMPTY_FORM });
        }
    }, [isOpen, editing]);

    // canPublishRecipes доступен только для корневых узлов:
    // - при создании — если parent === null
    // - при редактировании — если у узла parentId === null
    const isRootNode =
        editing !== null ? editing.parentId === null : parent === null;

    const validate = (): boolean => {
        const next: SpecializationFormErrors = {};
        if (form.name.trim() === '') {
            next.name = 'Название обязательно';
        }
        if (form.slug.trim() === '') {
            next.slug = 'Slug обязателен';
        } else if (!SLUG_REGEX.test(form.slug)) {
            next.slug = 'Только латиница, цифры и дефисы';
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        if (!validate()) {
            return;
        }
        const sortOrderNum = Number(form.sortOrder);
        onSubmit({
            name: form.name.trim(),
            slug: form.slug.trim(),
            sortOrder: Number.isFinite(sortOrderNum) ? sortOrderNum : 0,
            isActive: form.isActive,
            canPublishRecipes: isRootNode ? form.canPublishRecipes : null
        });
    };

    let title = 'Новый корневой узел';
    if (editing !== null) {
        title = 'Редактировать узел';
    } else if (parent !== null) {
        title = `Новый дочерний узел: ${parent.name}`;
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            testId="specialization-edit-modal"
        >
            <form
                onSubmit={handleSubmit}
                className="space-y-4"
                data-testid="specialization-edit-form"
            >
                <Input
                    label="Название"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    error={errors.name}
                    data-testid="specialization-form-name"
                    autoFocus
                />
                <Input
                    label="Slug"
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    placeholder="example-slug"
                    error={errors.slug}
                    data-testid="specialization-form-slug"
                />
                <Input
                    label="Порядок сортировки"
                    type="number"
                    value={form.sortOrder}
                    onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                    data-testid="specialization-form-sort-order"
                />
                <Switch
                    checked={form.isActive}
                    onChange={(next) => setForm({ ...form, isActive: next })}
                    label="Активен"
                    testId="specialization-form-is-active"
                />

                {isRootNode && (
                    <div
                        className="rounded border border-amber-200 bg-amber-50 p-3 space-y-2"
                        data-testid="specialization-form-can-publish-block"
                    >
                        <p className="text-xs font-medium uppercase text-amber-900">
                            Право публикации контента
                        </p>
                        <p className="text-xs text-amber-800">
                            Корневой узел задаёт правило для всех потомков. «Не задано» —
                            наследуется из контекста (по умолчанию разрешено).
                        </p>
                        <div className="flex flex-col gap-2 text-sm">
                            <label className="inline-flex items-center gap-2">
                                <input
                                    type="radio"
                                    name="canPublishRecipes"
                                    checked={form.canPublishRecipes === null}
                                    onChange={() =>
                                        setForm({ ...form, canPublishRecipes: null })
                                    }
                                    data-testid="specialization-form-can-publish-null"
                                />
                                Не задано
                            </label>
                            <label className="inline-flex items-center gap-2">
                                <input
                                    type="radio"
                                    name="canPublishRecipes"
                                    checked={form.canPublishRecipes === true}
                                    onChange={() =>
                                        setForm({ ...form, canPublishRecipes: true })
                                    }
                                    data-testid="specialization-form-can-publish-true"
                                />
                                Разрешено
                            </label>
                            <label className="inline-flex items-center gap-2">
                                <input
                                    type="radio"
                                    name="canPublishRecipes"
                                    checked={form.canPublishRecipes === false}
                                    onChange={() =>
                                        setForm({ ...form, canPublishRecipes: false })
                                    }
                                    data-testid="specialization-form-can-publish-false"
                                />
                                Запрещено
                            </label>
                        </div>
                    </div>
                )}

                {submitError !== null && submitError !== '' && (
                    <div
                        className="text-sm text-red-600"
                        data-testid="specialization-form-error"
                    >
                        {submitError}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                    >
                        Отмена
                    </button>
                    <button
                        type="submit"
                        disabled={isSaving}
                        data-testid="specialization-form-submit"
                        className="rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800 disabled:opacity-50"
                    >
                        {editing !== null ? 'Сохранить' : 'Создать'}
                    </button>
                </div>
            </form>
        </Modal>
    );
}
